import React, { useState } from 'react';
import { Box, Typography, SxProps, Theme } from '@mui/material';

interface HoverShortcutProps {
    /** Content rendered by default (e.g. traffic share percentage) */
    defaultContent: React.ReactNode;
    /** Content rendered on hover (e.g. "see ST" or icon + label) */
    hoverContent: React.ReactNode;
    /** Called when the hover shortcut is clicked */
    onClick: (e: React.MouseEvent) => void;
    /** Font size applied to both states */
    fontSize: number;
    /** Color of the default content (defaults to #919EAB) */
    defaultColor?: string;
    /** Override internal hover state (useful when hover is tracked by a parent) */
    forceHovered?: boolean;
    /** Extra sx for the root wrapper */
    sx?: SxProps<Theme>;
}

export default function HoverShortcut({
    defaultContent,
    hoverContent,
    onClick,
    fontSize,
    defaultColor = '#919EAB',
    forceHovered,
    sx,
}: HoverShortcutProps) {
    const [hovered, setHovered] = useState(false);
    const isHovered = forceHovered ?? hovered;

    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{ display: 'inline-flex', alignItems: 'center', ml: 1, ...sx as any }}
        >
            {isHovered ? (
                <Typography
                    variant="body2"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClick(e);
                    }}
                    sx={{
                        fontWeight: 'medium',
                        fontSize,
                        color: '#006C9C',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    {hoverContent}
                </Typography>
            ) : (
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 'medium',
                        fontSize,
                        color: defaultColor,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {defaultContent}
                </Typography>
            )}
        </Box>
    );
}
